import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft, ShoppingBag, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import DiscordLoginModal from "../components/DiscordLoginModal";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [settings, setSettings] = useState(null);
  const [fivemId, setFivemId] = useState("");
  const [method, setMethod] = useState("paypal");
  const [order, setOrder] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.product(slug).then(setProduct).catch(() => setError("Product not found."));
    api.settings().then(setSettings).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (user?.fivem_id) setFivemId(user.fivem_id);
  }, [user]);

  useEffect(() => {
    if (product?.is_free) setMethod("free");
  }, [product]);

  const handleStart = async () => {
    setError("");
    setMessage("");
    if (!user) {
      setLoginOpen(true);
      return;
    }
    if (!fivemId.trim()) {
      setError("Please enter your FiveM ID.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.createOrder({
        product_id: product.id,
        payment_method: method,
        fivem_id: fivemId.trim(),
      });
      setOrder(res.order);

      if (res.checkout.type === "free") {
        setMessage("Delivered! Staff have been notified on Discord.");
      } else if (res.checkout.type === "kofi") {
        window.open(res.checkout.url || "https://ko-fi.com/brokenrp", "_blank");
        setMessage("Kofi opened in a new tab. Once payment is complete, staff will confirm on Discord.");
      } else if (res.checkout.type === "mock_paypal") {
        // demo mode — auto capture
        const cap = await api.captureOrder(res.order.id, null);
        setOrder(cap);
        setMessage("DEMO checkout complete. Staff notified on Discord.");
      }
      // paypal type is handled by PayPalButtons via order state
    } catch (e) {
      setError(e?.response?.data?.detail || "Order failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!product) {
    return (
      <main className="pt-40 min-h-screen max-w-[1440px] mx-auto px-6 md:px-10 text-zinc-400 font-mono">
        {error || "Loading..."}
      </main>
    );
  }

  const price = product.is_free ? "FREE" : `£${Number(product.price).toFixed(2)}`;
  const paypalReady =
    settings?.paypal_configured &&
    settings?.paypal_client_id &&
    order &&
    order.payment_reference &&
    method === "paypal" &&
    !product.is_free;

  return (
    <main data-testid="product-page" className="pt-16 min-h-screen">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-10">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase text-zinc-400 hover:text-white"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
      </div>

      <section className="max-w-[1440px] mx-auto px-6 md:px-10 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* IMAGE */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
          className="lg:col-span-7 relative"
        >
          <div className="relative aspect-[4/3] md:aspect-[16/10] overflow-hidden border border-white/10">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_20%,rgba(255,255,255,0.06),transparent_60%)]" />
            <div className="absolute top-4 left-4 tag-chip">[ {product.category.replaceAll("-", " ").toUpperCase()} ]</div>
            {product.category === "businesses" && (
              <div className="absolute top-4 right-4 tag-chip border-[color:var(--accent-red)] text-[color:var(--accent-red)]">
                SUBSCRIPTION · MONTHLY
              </div>
            )}
            {product.stock != null && product.category !== "businesses" && (
              <div className="absolute top-4 right-4 tag-chip border-[color:var(--accent-red)] text-[color:var(--accent-red)]">
                LIMITED · STOCK {product.stock}
              </div>
            )}
          </div>
        </motion.div>

        {/* DETAILS */}
        <div className="lg:col-span-5">
          <div className="eyebrow mb-3">/ PRODUCT · #{product.id.slice(0, 6).toUpperCase()}</div>
          <h1 data-testid="product-title" className="font-display text-white text-5xl md:text-6xl tracking-tighter leading-[0.9] mb-3">
            {product.name}
          </h1>
          {product.tagline && <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-500">{product.tagline}</div>}

          <div className="flex items-baseline gap-3 mt-6">
            <div data-testid="product-price" className="font-display text-4xl text-white">{price}</div>
            {!product.is_free && (
              <div className="font-mono text-[11px] text-zinc-500">
                GBP · {product.category === "businesses" ? "per month" : "one-time"}
              </div>
            )}
          </div>

          {product.category === "businesses" && (
            <div className="mt-6 border-l-2 border-[color:var(--accent-red)] bg-[color:var(--accent-red)]/5 pl-4 py-3">
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--accent-red)] mb-1">
                / MONTHLY RE-PURCHASE
              </div>
              <p className="text-zinc-300 text-sm leading-relaxed">
                This is a monthly business rental. Your access lasts <span className="text-white">30 days</span> — after that you'll need to re-purchase to keep the business. Staff track payments manually via Discord; missed months mean the business goes back on the market.
              </p>
            </div>
          )}

          <p className="text-zinc-400 leading-relaxed mt-6">{product.description}</p>

          {product.features?.length > 0 && (
            <ul className="mt-6 space-y-2">
              {product.features.map((f, i) => (
                <li key={i} className="flex items-center gap-3 text-zinc-300 text-sm">
                  <Check className="w-3.5 h-3.5 text-[color:var(--accent-red)]" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}

          {/* PURCHASE PANEL */}
          <div className="mt-10 border border-white/10 p-6 bg-[color:var(--bg-surface)]">
            <div className="eyebrow mb-4">/ CHECKOUT</div>

            <label className="eyebrow block mb-2">FIVEM ID</label>
            <input
              data-testid="product-fivem-input"
              value={fivemId}
              onChange={(e) => setFivemId(e.target.value)}
              placeholder="steam:110000... or in-game name"
              className="terminal-input mb-5"
            />

            {!product.is_free && (
              <>
                <label className="eyebrow block mb-2">PAYMENT METHOD</label>
                <div className="grid grid-cols-2 gap-2 mb-5">
                  {[{ v: "paypal", label: "PayPal" }, { v: "kofi", label: "Ko-fi" }].map((m) => (
                    <button
                      key={m.v}
                      data-testid={`method-${m.v}`}
                      onClick={() => setMethod(m.v)}
                      className={`h-11 font-mono text-[11px] tracking-[0.24em] uppercase border transition-colors ${
                        method === m.v ? "border-white bg-white text-black" : "border-white/15 text-zinc-300 hover:border-white/40"
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            {error && <div className="font-mono text-[11px] text-[color:var(--accent-red)] mb-3">{error}</div>}
            {message && <div className="font-mono text-[11px] text-emerald-400 mb-3">{message}</div>}

            {!order && (
              <button
                onClick={handleStart}
                disabled={busy}
                data-testid="product-buy-btn"
                className="btn-brutal w-full h-12 border border-white text-white font-mono text-[11px] tracking-[0.24em] uppercase inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <ShoppingBag className="w-4 h-4" />
                {busy ? "Processing..." : product.is_free ? "Claim Free" : method === "kofi" ? "Open Ko-fi" : "Continue"}
              </button>
            )}

            {paypalReady && (
              <div className="mt-2">
                <PayPalScriptProvider
                  options={{
                    clientId: settings.paypal_client_id,
                    currency: "GBP",
                    intent: "capture",
                  }}
                >
                  <PayPalButtons
                    style={{ layout: "vertical", color: "black", shape: "rect", label: "pay" }}
                    createOrder={() => Promise.resolve(order.payment_reference)}
                    onApprove={async (data) => {
                      try {
                        const cap = await api.captureOrder(order.id, data.orderID);
                        setOrder(cap);
                        setMessage("Payment captured. Staff notified on Discord.");
                      } catch (e) {
                        setError(e?.response?.data?.detail || "Capture failed.");
                      }
                    }}
                    onError={() => setError("PayPal error. Please try again.")}
                  />
                </PayPalScriptProvider>
              </div>
            )}

            {order && (
              <div className="mt-6 font-mono text-[11px] text-zinc-400">
                ORDER · <span className="text-white">{order.id.slice(0, 8).toUpperCase()}</span> · <span className="text-white">{order.status.toUpperCase()}</span>
                <div className="mt-3">
                  <Link to="/account" className="inline-flex items-center gap-2 text-white underline">
                    View my orders <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <DiscordLoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
    </main>
  );
}
